/**
 * Створення декларації з податку на прибуток (ч.1387): організація (юрособи на загальній
 * системі), рік, період. Квартальна чи річна — сервер каже за карткою або доходом попереднього
 * року (понад 40 млн грн); у річного платника доступний лише період «рік».
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PERIOD_LABELS, profitTaxDeclarations } from '@/api/profitTaxDeclarations';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useDefaults } from '@/store/defaults';

export default function ProfitTaxDeclarationCreatePage() {
  const navigate = useNavigate();
  const { availableOrgs, hasPermissionInAnyOrg, totalAccessibleOrgs } =
    usePermissionsByOrg('documents.income_tax_calc.manage');
  const defaultOrgId = useDefaults((s) => s.defaultOrgId);
  const eligible = availableOrgs.filter((o) => o.entity_type !== 'FOP' && o.tax_system === 'GENERAL');
  const hidden = availableOrgs.length - eligible.length;

  const now = new Date();
  const prevQ = Math.floor((now.getMonth()) / 3) || 4;
  const prevY = Math.floor(now.getMonth() / 3) === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const [orgId, setOrgId] = useState('');
  const [year, setYear] = useState(prevY);
  const [quarter, setQuarter] = useState(prevQ);
  const [reportType, setReportType] = useState<'REPORTING' | 'REPORTING_NEW' | 'CORRECTING'>('REPORTING');
  const [corrYear, setCorrYear] = useState(prevY);
  const [corrQuarter, setCorrQuarter] = useState(prevQ);
  const [error, setError] = useState<string | null>(null);
  const isCorrection = reportType === 'CORRECTING';
  const effectiveOrg = orgId
    || (eligible.some((o) => o.id === defaultOrgId) ? defaultOrgId : '')
    || eligible[0]?.id || '';
  const perQ = useQuery({
    queryKey: ['profit-tax-periodicity', effectiveOrg, year],
    queryFn: () => profitTaxDeclarations.periodicity(effectiveOrg, year),
    enabled: !!effectiveOrg && year >= 2000,
  });
  const annual = perQ.data?.period === 'YEAR';
  const quarters = perQ.data?.quarters ?? [1, 2, 3, 4];

  const create = useMutation({
    mutationFn: () => profitTaxDeclarations.create({
      organization_id: effectiveOrg, period_year: year,
      period_quarter: annual && !isCorrection ? 4 : quarter,
      report_type: reportType,
      ...(isCorrection ? { corrected_period_year: corrYear, corrected_period_quarter: corrQuarter } : {}),
    }),
    onSuccess: (doc) => navigate(`/taxation/profit-tax-declarations/${doc.id}`),
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });

  if (!hasPermissionInAnyOrg) {
    return <div className="card">У вас немає прав створювати декларації у жодній з {totalAccessibleOrgs} організацій.</div>;
  }
  return (
    <div className="card" style={{ maxWidth: 680 }}>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Нова декларація з податку на прибуток</h2>
        <button style={{ marginLeft: 'auto' }} onClick={() => navigate('/taxation/profit-tax-declarations')}>до списку</button>
      </div>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginTop: 0 }}>
        Рядки 01 і 02 беруться зі звіту про фінансові результати (дохід без непрямих податків і
        фінансовий результат до оподаткування) наростаючим підсумком з 1 січня; податок — 18 %.
        Квартальна декларація — у платника з доходом за попередній рік понад 40 млн грн, інакше
        річна; змінити можна в картці організації. Різниці: для малих платників за замовчуванням не
        застосовуються (відмітка про рішення), для великих — додаток РІ заповнюється вручну.
        Фінансова звітність за період підтягується з документа «Фінзвіт» і йде в одному пакеті.
        Підпис і відправка — у Журналі Кабінету ДПС.
      </p>
      {eligible.length === 0 ? (
        <div style={{ color: 'var(--badge-amber-fg)' }}>
          Серед доступних організацій немає юросіб на загальній системі оподаткування.
        </div>
      ) : (
        <>
          <div className="field">
            <label>Організація{hidden > 0 ? ` (не платників податку на прибуток приховано: ${hidden})` : ''}</label>
            <select value={effectiveOrg} onChange={(e) => setOrgId(e.target.value)}>
              {eligible.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Тип</label>
            <select value={reportType} onChange={(e) => setReportType(e.target.value as any)}>
              <option value="REPORTING">Звітна</option>
              <option value="REPORTING_NEW">Звітна нова</option>
              <option value="CORRECTING">Уточнююча</option>
            </select>
          </div>
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 9' }}>
              <label>{isCorrection ? 'Період подання' : 'Податковий (звітний) період'}</label>
              {annual && !isCorrection
                ? <input value="рік (річний платник)" disabled />
                : (
                  <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
                    {quarters.map((q) => <option key={q} value={q}>{PERIOD_LABELS[q]}</option>)}
                  </select>
                )}
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>Рік</label>
              <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
            </div>
          </div>
          {perQ.data && (
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
              {perQ.data.period === 'QUARTER' ? 'Квартальна декларація' : 'Річна декларація'} — {perQ.data.reason}
              {perQ.data.source === 'card' ? '' : ' (змінити можна в картці організації)'}.
            </div>
          )}
          {isCorrection && (
            <div className="doc-grid">
              <div className="field" style={{ gridColumn: 'span 9' }}>
                <label>Період, що УТОЧНЮЄТЬСЯ</label>
                <select value={corrQuarter} onChange={(e) => setCorrQuarter(Number(e.target.value))}>
                  {[1, 2, 3, 4].map((q) => <option key={q} value={q}>{PERIOD_LABELS[q]}</option>)}
                </select>
              </div>
              <div className="field" style={{ gridColumn: 'span 3' }}>
                <label>Рік</label>
                <input type="number" value={corrYear} onChange={(e) => setCorrYear(Number(e.target.value))} />
              </div>
            </div>
          )}
          {error && <div style={{ color: 'var(--badge-red-fg)', marginBottom: 8 }}>{error}</div>}
          <button className="primary" disabled={create.isPending || !effectiveOrg} onClick={() => create.mutate()}>
            створити
          </button>
        </>
      )}
    </div>
  );
}
